// Sign-in at an identity provider (Google, Microsoft, an organization's directory), browser side.
//
// The tab that starts it keeps a secret (`verifier`) and tells the API only its hash. When the
// provider's flow returns with a one-time code, that code is worth nothing without the secret — so
// a link somebody else prepared (their own sign-in, or one meant to accept an invitation) cannot
// sign this browser in or accept anything.
//
// A transaction has a purpose: a sign-in (default), or — for the signed-in person — connecting the
// provider identity to their account ("link") or proving it is them ("reauth"). Those two return
// to the screen they were started from.
import {
  authApi,
  AuthApiError,
  CallerTokens,
  InvitationRef,
  SessionPayload,
  SignInMethods,
  SsoPurpose
} from './authApi';
import { linkSecret } from './linkSecrets';
import { freshAccessToken, readTokens } from './session';

const KEY = 'dosen.auth.sso';
const ACCEPT_ROUTE = '/accept-invitation';

interface PendingProviderSignIn {
  verifier: string;
  /** started from an invitation: finishing it accepts the invitation this tab was opened with */
  invitation: boolean;
  purpose?: SsoPurpose;
  /** link / reauth: the route (hash path) to come back to */
  returnTo?: string;
}

export type ProviderTransactionOutcome =
  | { purpose: 'link'; methods: SignInMethods }
  | { purpose: 'reauth'; reauthUntil: string };

const base64Url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

function readPending(): PendingProviderSignIn | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    return parsed && typeof parsed.verifier === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/** The invitation this tab holds, when the pending sign-in was started from it. */
export function pendingInvitation(): InvitationRef | null {
  if (!readPending()?.invitation) return null;
  const token = linkSecret(ACCEPT_ROUTE, 'token');
  const ticket = linkSecret(ACCEPT_ROUTE, 'ticket');
  return token ? { token } : ticket ? { ticket } : null;
}

/** True when the pending sign-in was started from an invitation (where a refusal is shown). */
export const startedFromInvitation = (): boolean => readPending()?.invitation === true;

/** What the pending transaction is for ("signin" when there is none). */
export const pendingPurpose = (): SsoPurpose => readPending()?.purpose ?? 'signin';

/** The route a pending link / reauth transaction returns to (the app's root otherwise). */
export const pendingReturnTo = (): string => readPending()?.returnTo || '/';

/** The signed-in person's tokens, renewed first when about to expire (null: no session to speak for). */
async function callerTokens(): Promise<CallerTokens | null> {
  const accessToken = await freshAccessToken();
  const tokens = readTokens();
  return accessToken && tokens ? { accessToken, idToken: tokens.id_token, region: tokens.region } : null;
}

/**
 * Starts a sign-in at `connection` and resolves with the address to open next (the provider's own
 * page). Throws AuthApiError when the API refuses; a browser without Web Crypto cannot start one.
 * `transaction`: a link / reauth transaction of the signed-in person instead of a sign-in.
 */
export async function startProviderSignIn(
  connection: string,
  fromInvitation: boolean,
  transaction?: { purpose: 'link' | 'reauth'; returnTo: string }
): Promise<string> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const pending: PendingProviderSignIn = {
    verifier,
    invitation: fromInvitation,
    ...(transaction ? { purpose: transaction.purpose, returnTo: transaction.returnTo } : {})
  };
  sessionStorage.setItem(KEY, JSON.stringify(pending));
  const { url } = await authApi.startSso(
    {
      connection,
      returnTo: `${window.location.origin}${window.__APP_BASE__ || ''}`,
      challenge: base64Url(new Uint8Array(digest)),
      ...(transaction ? { purpose: transaction.purpose } : {})
    },
    transaction ? (await callerTokens()) ?? undefined : undefined
  );
  return url;
}

/** The pending sign-in is over (finished, refused or abandoned). */
export function forgetProviderSignIn(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
}

const noPending = (): AuthApiError =>
  new AuthApiError('sso_expired', 410, 'That sign-in took too long or was already used. Start again.');

/** Trades the one-time code the provider flow came back with — and this tab's secret — for the session. */
export async function finishProviderSignIn(code: string): Promise<SessionPayload> {
  const verifier = readPending()?.verifier;
  // No secret in this tab: the sign-in was not started here, and the API will not finish it.
  if (!verifier) throw noPending();
  try {
    return await authApi.completeSso({ code, verifier, ...(pendingInvitation() ?? {}) });
  } finally {
    forgetProviderSignIn();
  }
}

/** Finishes a link / reauth transaction of the signed-in person: the code, the secret, and their tokens. */
export async function finishProviderTransaction(code: string): Promise<ProviderTransactionOutcome> {
  const pending = readPending();
  const caller = await callerTokens();
  if (!pending?.verifier || !pending.purpose || pending.purpose === 'signin' || !caller) throw noPending();
  try {
    if (pending.purpose === 'link') {
      return { purpose: 'link', methods: await authApi.completeSsoLink({ code, verifier: pending.verifier }, caller) };
    }
    const { reauthUntil } = await authApi.completeSsoReauth({ code, verifier: pending.verifier }, caller);
    return { purpose: 'reauth', reauthUntil };
  } finally {
    forgetProviderSignIn();
  }
}
